'use client';

import Link from 'next/link';
import type { PointerEvent, ReactNode } from 'react';

import { externalProps } from './Links';

/**
 * How much faster a door's pattern runs while the card is pointed at. A playback
 * rate rather than a second CSS duration: changing `animation-duration`
 * re-derives the animation's position from its original start time, so the phase
 * jumps. `updatePlaybackRate` holds the current time.
 */
const HOVER_RATE = 2;

/**
 * Retimes the pattern animation and nothing else.
 *
 * `getAnimations({ subtree: true })` reaches the ::before that carries the
 * pattern, which no ref can. The filter keeps this off the border and lift
 * transitions in the same list. Under reduced motion nothing is animating, so it
 * needs no second guard.
 */
const retime = (rate: number) => (event: PointerEvent<HTMLElement>) => {
  for (const animation of event.currentTarget.getAnimations({ subtree: true })) {
    const effect = animation.effect;
    if ('animationName' in animation && effect instanceof KeyframeEffect && effect.pseudoElement === '::before') {
      animation.updatePlaybackRate(rate);
    }
  }
};

const slowDown = retime(1);

/*
 * Touch has no hover to leave. A tap fires pointerenter and, if the finger lifts
 * without moving off, no pointerleave follows, so the pattern stays at double
 * speed for as long as the page is open.
 */
const speedUp = (event: PointerEvent<HTMLElement>) => {
  if (event.pointerType === 'touch') return;
  retime(HOVER_RATE)(event);
};

/**
 * A door's anchor, and the only part of the card that runs in the browser. Split
 * out so the card stays a server component. A component type cannot cross into a
 * client bundle, so everything the card draws arrives as children.
 */
export const DoorSurface = ({
  href,
  trackingId,
  className,
  children,
}: {
  href: string;
  trackingId?: string;
  className?: string;
  children: ReactNode;
}) => (
  <Link
    href={href}
    data-cta={trackingId}
    {...externalProps(href)}
    onPointerEnter={speedUp}
    onPointerLeave={slowDown}
    /* A pointer the browser takes away, such as a touch that turns into a
       scroll, sends no pointerleave either. */
    onPointerCancel={slowDown}
    className={className}>
    {children}
  </Link>
);
